import React, { useEffect, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useParams, useNavigate } from 'react-router-dom';
import { useDataFetch } from '../../Hooks/Use-data-fetch';
import {
  Button,
  Card,
  CardBody,
  Container,
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  Col,
} from 'reactstrap';
import { FaInfoCircle, FaProjectDiagram, FaUsers, FaUserFriends, FaLayerGroup } from 'react-icons/fa';
import { API_BASE_URL, apiClient } from '../../api/api';
import { getLayoutedElements } from '../../utils/layout';

const CustomNode = ({ data }) => {
  const { name, code, isActive, onInfoClick, onNetworkClick, hasChildren } = data;

  return (
    <div
      style={{
        width: 150,
        height: 70,
        borderRadius: '10px',
        backgroundColor: '#fff',
        border: `2px solid ${isActive ? '#22C55E' : '#EF4444'}`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: 8,
        position: 'relative',
        boxShadow: '0 0 5px rgba(0,0,0,0.2)',
        textAlign: 'center',
        padding: 2,
      }}
    >
      <p style={{ fontSize: 12, margin: 0 }}>
        <strong>{code}</strong>
        <br />
        {name}
      </p>
      <div style={{ position: 'absolute', bottom: -20, display: 'flex', gap: 6 }}>
        <FaInfoCircle size={16} style={{ cursor: 'pointer' }} onClick={onInfoClick} title="Ver info" />
        {hasChildren && (
          <FaProjectDiagram
            size={16}
            style={{ cursor: 'pointer' }}
            onClick={onNetworkClick}
            title="Ver red"
          />
        )}
      </div>
      <Handle type="source" position={Position.Bottom} />
      <Handle type="target" position={Position.Top} />
    </div>
  );
};

export const ReferralNetwork = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getData } = useDataFetch();
  const [modalData, setModalData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loadedParents, setLoadedParents] = useState(new Set());
  const [initialized, setInitialized] = useState(false);

  const { data: userData, isLoading: isUserLoading } = getData(
    ['user-by-id', id],
    `/users/byId/${id}`,
    { enabled: !!id }
  );
  const mainUser = userData?.data?.user;
  console.log(mainUser);
  

  const { data: networkData, isLoading: isNetworkLoading } = getData(
    ['user-network', id],
    `/users/admin/network/with/id?userid=${id}`,
    { enabled: !!id }
  );

  const referralList = networkData?.data?.data ?? networkData?.data?.user ?? networkData?.data ?? [];

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const onLayout = (nodesToLayout, edgesToLayout) => {
    if (!nodesToLayout || nodesToLayout.length === 0) return;
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      nodesToLayout,
      edgesToLayout
    );
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  };

  const toggleSubNetwork = async (parentId) => {
    if (loadedParents.has(parentId)) {
      const childrenIdsToRemove = new Set();
      const queue = [parentId];
      const visited = new Set([parentId]);

      while (queue.length > 0) {
        const currentId = queue.shift();
        const children = edges.filter(e => e.source === currentId).map(e => e.target);
        for (const childId of children) {
          if (!visited.has(childId)) {
            childrenIdsToRemove.add(childId);
            queue.push(childId);
            visited.add(childId);
          }
        }
      }

      const remainingNodes = nodes.filter(n => !childrenIdsToRemove.has(n.id));
      const remainingEdges = edges.filter(e => !childrenIdsToRemove.has(e.target));

      setLoadedParents(prev => {
        const newSet = new Set(prev);
        childrenIdsToRemove.forEach(id => newSet.delete(id));
        newSet.delete(parentId);
        return newSet;
      });

      onLayout(remainingNodes, remainingEdges);
      return;
    }

    try {
      const response = await apiClient.get(`/users/admin/network/with/id?userid=${parentId}`, {
        headers: {
          Authorization: `${localStorage.getItem("token") || ""}`,
          Accept: "application/json",
        },
      });

      const referrals = response?.data?.data?.data ?? response?.data?.data?.user ?? response?.data?.data ?? [];

      if (referrals.length === 0) return;

      const newNodes = referrals
        .filter(ref => !nodes.some(n => n.id === ref._id))
        .map((ref) => ({
          id: ref._id,
          type: 'custom',
          position: { x: 0, y: 0 },
          data: {
            name: ref.name,
            code: ref.multyhealth_code,
            isActive: ref.network?.loyalty_status === true,
            hasChildren: ref.referenced_by_count > 0,
            onInfoClick: () => {
              setModalData(ref);
              setModalOpen(true);
            },
            onNetworkClick: () => toggleSubNetwork(ref._id),
          },
        }));

      const newEdges = referrals
        .filter(ref => !edges.some(e => e.source === parentId && e.target === ref._id))
        .map((ref) => ({
          id: `e-${parentId}-${ref._id}`,
          source: parentId,
          target: ref._id,
        }));

      setNodes((currentNodes) => {
        const updatedNodes = [...currentNodes, ...newNodes];
        setEdges((currentEdges) => {
          const updatedEdges = [...currentEdges, ...newEdges];
          onLayout(updatedNodes, updatedEdges);
          return updatedEdges;
        });
        return updatedNodes;
      });

      setLoadedParents((prev) => new Set(prev).add(parentId));
    } catch (error) {
      console.error("Error loading sub network:", error);
    }
  };

  // Construir el árbol - Esperar a que los datos estén listos
  useEffect(() => {
    // No hacer nada si ya se inicializó o no hay usuario
    if (initialized || !mainUser) return;

    // Esperar a que ambas peticiones terminen
    if (isUserLoading || isNetworkLoading) return;

    console.log('Datos listos - mainUser:', mainUser);
    console.log('Datos listos - referralList:', referralList);

    const mainNode = {
      id: mainUser.id,
      type: 'custom',
      position: { x: 0, y: 0 },
      data: {
        name: mainUser.name,
        code: mainUser.multyhealth_code,
        isActive: mainUser.network?.loyalty_status === true,
        hasChildren: (referralList && referralList.length > 0) || mainUser.referenced_by_count > 0,
        onInfoClick: () => {
          setModalData(mainUser);
          setModalOpen(true);
        },
        onNetworkClick: () => toggleSubNetwork(mainUser.id),
      },
    };

    let initialNodes = [mainNode];
    let initialEdges = [];

    if (referralList && referralList.length > 0) {
      console.log('Agregando referidos:', referralList.length);

      const referralNodes = referralList.map((ref) => ({
        id: ref._id,
        type: 'custom',
        position: { x: 0, y: 0 },
        data: {
          name: ref.name,
          code: ref.multyhealth_code,
          isActive: ref.network?.loyalty_status === true,
          hasChildren: ref.referenced_by_count > 0,
          onInfoClick: () => {
            setModalData(ref);
            setModalOpen(true);
          },
          onNetworkClick: () => toggleSubNetwork(ref._id),
        },
      }));

      const referralEdges = referralList.map((ref) => ({
        id: `e-${mainUser.id}-${ref._id}`,
        source: mainUser.id,
        target: ref._id,
      }));

      initialNodes = [mainNode, ...referralNodes];
      initialEdges = referralEdges;

      setLoadedParents(new Set([mainUser.id]));
    }

    onLayout(initialNodes, initialEdges);
    setInitialized(true);
  }, [mainUser, referralList, isUserLoading, isNetworkLoading]);

  if (isUserLoading || isNetworkLoading) {
    return <div className="text-center p-4">Cargando red de referidos...</div>;
  }

  if (!mainUser) {
    return <div className="text-center p-4">Usuario no encontrado</div>;
  }

  const totalDirects = referralList?.length || 0;
  const totalNetwork = mainUser?.user_network?.length || totalDirects;

  return (
    <Container fluid className="mt-4" style={{ height: 'calc(100vh - 2rem)' }}>
      <Card className="border-0 shadow-sm mb-4">
        <CardBody>
          <Row className="align-items-center">
            <Col md="3" className="text-center text-md-start">


              <img
                src={`${API_BASE_URL}/users/show/photo/${mainUser?.photo || ""}`}
                alt={mainUser?.name || "Usuario"}
                style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "2px solid #e9ecef",
                }}

              />


            </Col>
            <Col md="6">
              <h5 className="fw-bold mb-1">{mainUser.name}</h5>
              <p className="text-muted mb-1">Código: {mainUser.multyhealth_code}</p>
              <p className="mb-0">
                <span className="badge bg-success me-2">Rango Actual: {mainUser.network?.rank_id || 'Oro'}</span>
                <span className="badge bg-info">Puntos Actuales: {mainUser.network?.loyalty_points || '0'}</span>
              </p>
            </Col>
            <Col md="3">
              <div className="d-flex gap-3 justify-content-md-end">
                <div className="text-center">
                  <FaUserFriends size={24} className="text-primary" />
                  <p className="mb-0 fw-bold">{totalDirects}</p>
                  <small className="text-muted">Total Directos</small>
                </div>
                <div className="text-center">
                  <FaUsers size={24} className="text-success" />
                  <p className="mb-0 fw-bold">{totalNetwork}</p>
                  <small className="text-muted">Total Red</small>
                </div>
                <div className="text-center">
                  <FaLayerGroup size={24} className="text-warning" />
                  <p className="mb-0 fw-bold">7</p>
                  <small className="text-muted">Niveles</small>
                </div>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card className="h-100 d-flex flex-column">
        <CardBody className="flex-grow-1 d-flex flex-column">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Red Descendente</h5>
            <Button color="secondary" onClick={() => navigate(-1)}>
              Volver
            </Button>
          </div>
          <div style={{ flexGrow: 1, minHeight: 0 }}>
            <ReactFlow
              nodeTypes={{ custom: CustomNode }}
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              fitView
            >
              <MiniMap />
              <Controls />
              <Background />
            </ReactFlow>
          </div>
        </CardBody>
      </Card>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
        <ModalHeader toggle={() => setModalOpen(false)}>
          Información del Usuario
        </ModalHeader>
        <ModalBody>
          {modalData && (
            <div className="text-center">
              <img
                src={`${API_BASE_URL}/users/show/photo/${modalData.photo || ""}`}
                alt="Foto"
                className="rounded-circle mb-3"
                width={80}
                height={80}
              />
              <h5>{modalData.name}</h5>
              <p>Email: {modalData.email}</p>
              <p>Teléfono: {modalData.cellphone || 'N/A'}</p>
              <p>
                Estado:{' '}
                <strong>
                  {modalData.network?.loyalty_status ? 'Activo' : 'Inactivo'}
                </strong>
              </p>
            </div>
          )}
        </ModalBody>
      </Modal>
    </Container>
  );
};